import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import classNames from 'classnames';
import Container from 'components/Container';
import { BlockLoader } from 'components/Loaders';
import Seo from 'components/Seo';
import PageWrap from './components/PageWrap';
import Calculator from './components/Calculator';
import HowChoose from './components/HowChoose';
import { useStores } from 'hooks/useStores';
import CollectionPageJsonLd from 'components/JsonLd/CollectionPageJsonLd';
import FAQJsonLd from 'components/JsonLd/FAQJsonLd';
import Breadcrumbs from 'components/Breadcrumbs';
import RelatedCategories from 'components/RelatedCategories';
import RelatedArticles from 'components/RelatedArticles';
import Goods from './Goods';
import { HtmlContent } from '../../utils';

import '../../styles/calculator.css';
import '../../styles/calculator-how.css';

const TABS = {
  goods: 'g',
  calculator: 'c',
  howChoose: 'h'
};

const TABS_KEY = 'tab';

const FILTER_PARAMS = ['price_min', 'price_max', 'in_stock', 'param'];

const NGDetail = () => {
  const [currentTab, setCurrentTab] = useState(TABS.calculator);
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api } = useStores();

  const { data, isLoading } = useSWR(
    { slug, _key: `ng-detail-${slug}` },
    api.newCatalog.getNomenclatureGroupSlug
  );

  const hasActiveFilters = FILTER_PARAMS.some((k) => searchParams.get(k));

  const seoForPage = hasActiveFilters && data?.seo
    ? {
        ...data.seo,
        canonical_url: `${window.location.origin}/ng/${slug}/`,
        noindex: true,
      }
    : data?.seo;

  const getTabClasses = (tabName) => {
    return classNames({
      'calculator-main-buttons__item-calc': true,
      'calc-button-active': currentTab === tabName
    });
  };

  const handleClickTab = (tabName) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(TABS_KEY, tabName);
      return next;
    });
  };

  const checkAndSetTab = async (tabParam) => {
    if (tabParam === TABS.calculator && data?.calculator_type !== null) {
      setCurrentTab(TABS.calculator);
      return;
    }

    if (tabParam === TABS.howChoose && data?.display_how_choice) {
      setCurrentTab(TABS.howChoose);
      return;
    }

    if (tabParam === TABS.goods) {
      setCurrentTab(TABS.goods);
      return;
    }

    if (data?.calculator_type !== null) {
      setCurrentTab(TABS.calculator);
    } else {
      setCurrentTab(TABS.goods);
    }
  };

  useEffect(() => {
    const tabParam = searchParams.get(TABS_KEY);
    checkAndSetTab(tabParam);
  }, [searchParams, slug, data]);

  if (isLoading) {
    return (
      <Container>
        <BlockLoader height={306} />;
      </Container>
    );
  }

  return (
    <>
      <Seo seo={seoForPage} pageImage={data?.image} />
      {data && (
        <>
          <CollectionPageJsonLd group={data} />
          <FAQJsonLd items={data?.faq_items} />
        </>
      )}
      <Breadcrumbs type="ng" slug={slug} />
      <PageWrap title={data?.seo?.h1 || data?.title || ''} slug={slug}>
        <div className="calculator-main-buttons">
          {data?.calculator_type !== null ? (
            <button
              type="button"
              className={getTabClasses(TABS.calculator)}
              onClick={() => handleClickTab(TABS.calculator)}>
              <h2>Калькулятор</h2>
            </button>
          ) : null}

          <button
            type="button"
            className={getTabClasses(TABS.goods)}
            onClick={() => handleClickTab(TABS.goods)}>
            <h2>Товары</h2>
          </button>

          {data?.display_how_choice === true ? (
            <button
              type="button"
              className={getTabClasses(TABS.howChoose)}
              onClick={() => handleClickTab(TABS.howChoose)}>
              <h2>Как выбрать?</h2>
            </button>
          ) : null}
        </div>

        <Goods isActive={currentTab === TABS.goods} availableFilters={data?.available_filters} />
        <Calculator isActive={currentTab === TABS.calculator} type={data?.calculator_type ?? null} />
        <HowChoose isActive={currentTab === TABS.howChoose} />
      </PageWrap>
      {data?.seo?.seo_content && (
        <HtmlContent
          content={data.seo.seo_content}
          className="category-seo-content"
        />
      )}
      <RelatedCategories slug={slug} />
      <RelatedArticles articles={data?.related_articles} />
    </>
  );
};

export default NGDetail;
